import type {
  ErrorMetricEntry,
  ForkliftStats,
  ScenarioDailyByFork,
  ScenarioDailyCounts,
  ScenarioDailyStats,
  ScenarioDetail,
  ScenarioRunInterval,
  ScenarioStats,
} from '@/types/forklift'
import { RUN_FLAG } from '@/common/scenarioRunTimeline'

// メトリクス画面で複数機体 (「すべて」/ 任意の組み合わせ) を選んだときの合算。
// API は機体単位 (GET /forklifts/{id}/stats, /scenario-stats) なので、機体ごとに取得した
// 応答をクライアント側で 1 機体分と同じ形にまとめ、既存の表示コンポーネントをそのまま使う。
// 1 機体だけのときはそのまま返す (合算による丸めや並び替えを入れない)。

export const MULTI_FORK_ID_SEPARATOR = ','

export function joinForkIds(forkIds: string[]): string {
  return forkIds.join(MULTI_FORK_ID_SEPARATOR)
}

export function splitForkIds(value: string | null | undefined): string[] {
  if (!value) return []
  return Array.from(
    new Set(
      value
        .split(MULTI_FORK_ID_SEPARATOR)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
    )
  )
}

// 日別の走行距離・稼働時間は日付ごとに足し合わせる。稼働率は機体平均。
// 日別走行距離グラフを機体別に積み上げられるよう distance_by_fork も持つ
export function mergeForkliftStats(list: ForkliftStats[]): ForkliftStats {
  if (list.length === 1) return list[0]
  const byDate = new Map<string, ForkliftStats['data'][number]>()
  for (const stats of list) {
    for (const d of stats.data) {
      let prev = byDate.get(d.date)
      if (!prev) {
        prev = {
          date: d.date,
          operating_hours: 0,
          idle_hours: 0,
          utilization_rate: 0,
          distance_km: 0,
          alerts_count: 0,
          distance_by_fork: {},
        }
        byDate.set(d.date, prev)
      }
      prev.operating_hours += d.operating_hours
      prev.idle_hours += d.idle_hours
      prev.distance_km += d.distance_km
      prev.alerts_count += d.alerts_count
      prev.utilization_rate += d.utilization_rate
      prev.distance_by_fork![stats.fork_id] = d.distance_km
    }
  }
  const n = list.length
  const data = Array.from(byDate.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({
      ...d,
      distance_km: round2(d.distance_km),
      operating_hours: round2(d.operating_hours),
      idle_hours: round2(d.idle_hours),
      utilization_rate: round2(d.utilization_rate / n),
    }))
  const first = list[0]
  return {
    fork_id: joinForkIds(list.map((s) => s.fork_id)),
    period: first?.period ?? { start: '', end: '' },
    granularity: first?.granularity ?? 'day',
    data,
    summary: {
      avg_utilization: round2(
        list.reduce((s, x) => s + x.summary.avg_utilization, 0) / n
      ),
      total_operating_hours: round2(
        list.reduce((s, x) => s + x.summary.total_operating_hours, 0)
      ),
      total_distance_km: round2(
        list.reduce((s, x) => s + x.summary.total_distance_km, 0)
      ),
    },
  }
}

// 同じシナリオ ID は 1 行にまとめ (実行回数などは合算)、どの機体で実行したかを fork_ids に残す。
// 日別の描画は「シナリオ別」ではなく「機体別」に簡略化する:
//   - fork_daily: 日別・機体別の実行数 (積み上げグラフ用。凡例は機体)
//   - daily[].runs: 1 機体 1 日の実行を、60 分以上空かずに続く区間ごとの帯にまとめる (色は機体)。
//     実行回数と実行時間合計は count / runMin に持たせ、終了未観測の実行は別の帯にする
// 全機体 × 1 年で実行ごとの帯 (数万 rect) とシナリオ × 日数のセルを描くとブラウザが落ちるため
export function mergeScenarioStats(list: ScenarioStats[]): ScenarioStats {
  if (list.length === 1) return list[0]

  const scenarioMap = new Map<string, ScenarioDetail>()
  for (const stats of list) {
    for (const s of stats.scenarios) {
      const prev = scenarioMap.get(s.id)
      if (!prev) {
        scenarioMap.set(s.id, {
          ...s,
          trajectories: s.trajectories ? [...s.trajectories] : null,
          fork_ids: [stats.fork_id],
        })
        continue
      }
      prev.executions += s.executions
      prev.completions += s.completions
      prev.cancellations += s.cancellations
      prev.duration_ms += s.duration_ms
      prev.error_stops = sumOptional(prev.error_stops, s.error_stops)
      prev.isPlaybackScenario = prev.isPlaybackScenario || s.isPlaybackScenario
      if (!prev.name && s.name) prev.name = s.name
      if (!prev.trajectories && s.trajectories) {
        prev.trajectories = [...s.trajectories]
      }
      prev.fork_ids = [...(prev.fork_ids ?? []), stats.fork_id]
    }
  }
  const scenarios = Array.from(scenarioMap.values()).sort(
    (a, b) => b.executions - a.executions
  )

  const dailyMap = new Map<string, ScenarioDailyStats>()
  const forkDailyMap = new Map<string, ScenarioDailyByFork>()
  list.forEach((stats, lane) => {
    for (const day of stats.daily) {
      let merged = dailyMap.get(day.date)
      if (!merged) {
        merged = { date: day.date }
        dailyMap.set(day.date, merged)
      }
      let forkDay = forkDailyMap.get(day.date)
      if (!forkDay) {
        forkDay = { date: day.date, counts: {} }
        forkDailyMap.set(day.date, forkDay)
      }
      const forkCounts: ScenarioDailyCounts = {
        executions: 0,
        completions: 0,
        cancellations: 0,
      }
      for (const [key, value] of Object.entries(day)) {
        if (key === 'date') continue
        if (key === 'runs') {
          const bands = summarizeRuns(
            (value as ScenarioRunInterval[] | undefined) ?? [],
            lane
          )
          if (bands.length > 0) {
            merged.runs = [...(merged.runs ?? []), ...bands]
          }
          continue
        }
        const counts = value as ScenarioDailyCounts | undefined
        if (!counts || typeof counts !== 'object') continue
        forkCounts.executions += counts.executions
        forkCounts.completions += counts.completions
        forkCounts.cancellations += counts.cancellations
        const prev = merged[key] as ScenarioDailyCounts | undefined
        merged[key] = prev
          ? {
              executions: prev.executions + counts.executions,
              completions: prev.completions + counts.completions,
              cancellations: prev.cancellations + counts.cancellations,
            }
          : { ...counts }
      }
      if (
        forkCounts.executions ||
        forkCounts.completions ||
        forkCounts.cancellations
      ) {
        forkDay.counts[stats.fork_id] = forkCounts
      }
    }
  })
  const daily = Array.from(dailyMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  )
  const forkDaily = Array.from(forkDailyMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  )

  const errorByCode = new Map<number, ErrorMetricEntry>()
  let hasErrorStats = false
  let stopCount = 0
  for (const stats of list) {
    if (!stats.error_stats) continue
    hasErrorStats = true
    stopCount += stats.error_stats.stop_count
    for (const e of stats.error_stats.breakdown) {
      const prev = errorByCode.get(e.code)
      if (!prev) {
        errorByCode.set(e.code, { ...e })
        continue
      }
      prev.occurred += e.occurred
      prev.downtime_ms += e.downtime_ms
      prev.stops += e.stops
      if (prev.level == null && e.level != null) prev.level = e.level
      if (!prev.message && e.message) prev.message = e.message
    }
  }
  const breakdown = Array.from(errorByCode.values()).sort(
    (a, b) => b.stops - a.stops || b.occurred - a.occurred
  )

  const first = list[0]
  return {
    fork_id: joinForkIds(list.map((s) => s.fork_id)),
    period: first?.period ?? { start: '', end: '' },
    total_executions: sum(list, (s) => s.total_executions),
    total_completions: sum(list, (s) => s.total_completions),
    total_cancellations: sum(list, (s) => s.total_cancellations),
    total_error_stops: sumOptionalAll(list, (s) => s.total_error_stops),
    total_pause_count: sumOptionalAll(list, (s) => s.total_pause_count),
    total_pause_ms: sumOptionalAll(list, (s) => s.total_pause_ms),
    total_transports: sumOptionalAll(list, (s) => s.total_transports),
    total_duration_ms: sum(list, (s) => s.total_duration_ms),
    cumulative_duration_ms: sum(list, (s) => s.cumulative_duration_ms),
    error_stats: hasErrorStats
      ? { stop_count: stopCount, breakdown }
      : undefined,
    scenarios,
    daily,
    fork_ids: list.map((s) => s.fork_id),
    fork_daily: forkDaily,
  }
}

// 連続実行とみなす最大の空き時間 (分)。これ以上空いたら別の帯にする
// (昼休みや夜間の停止を 1 本に塗りつぶさないため)
const RUN_BLOCK_GAP_MIN = 60

// 1 機体 1 日分の実行区間を、終了確定分と終了未観測分それぞれについて、
// 間隔が RUN_BLOCK_GAP_MIN 未満で続く実行を 1 本の帯にまとめる (初回開始〜最終終了を
// 1 本にすると長い空き時間も塗られてしまう)。count / runMin に回数と実行時間合計を残す
function summarizeRuns(
  runs: ScenarioRunInterval[],
  lane: number
): ScenarioRunInterval[] {
  const closed: [number, number, number][] = [] // [start, end, flag]
  const open: [number, number, number][] = []
  for (const run of runs) {
    const [s, e, , flag] = run
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue
    const start = Math.max(0, Math.min(1440, s))
    const end = Math.max(0, Math.min(1440, Math.max(s, e)))
    ;(flag === RUN_FLAG.open ? open : closed).push([start, end, flag])
  }
  const out: ScenarioRunInterval[] = []
  for (const [list, isOpen] of [
    [closed, false],
    [open, true],
  ] as const) {
    list.sort((a, b) => a[0] - b[0])
    let block: {
      start: number
      end: number
      runMin: number
      count: number
      hasError: boolean
    } | null = null
    const flush = () => {
      if (!block) return
      out.push([
        block.start,
        block.end,
        -1,
        isOpen
          ? RUN_FLAG.open
          : block.hasError
            ? RUN_FLAG.errorStopped
            : RUN_FLAG.completed,
        lane,
        isOpen ? 0 : block.runMin,
        block.count,
      ])
      block = null
    }
    for (const [start, end, flag] of list) {
      if (block && start - block.end >= RUN_BLOCK_GAP_MIN) flush()
      if (!block) {
        block = { start, end, runMin: 0, count: 0, hasError: false }
      }
      block.end = Math.max(block.end, end)
      block.runMin += end - start
      block.count += 1
      if (flag === RUN_FLAG.errorStopped) block.hasError = true
    }
    flush()
  }
  return out
}

function round2(v: number): number {
  return Math.round(v * 100) / 100
}

function sum<T>(list: T[], pick: (x: T) => number): number {
  return list.reduce((s, x) => s + pick(x), 0)
}

// どの機体にも値が無ければ undefined (未対応機体のみ) のまま、1 機体でもあれば合算
function sumOptional(
  a: number | undefined,
  b: number | undefined
): number | undefined {
  if (a == null && b == null) return undefined
  return (a ?? 0) + (b ?? 0)
}

function sumOptionalAll<T>(
  list: T[],
  pick: (x: T) => number | undefined
): number | undefined {
  return list.reduce<number | undefined>(
    (acc, x) => sumOptional(acc, pick(x)),
    undefined
  )
}
